import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { API_URL, API_BASE_URL } from "../config/api";
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Award,
  Target,
  AlertTriangle, 
  CheckCircle,
  Clock,
  RefreshCw,
  User,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  BarChart3,
  Star,
  DollarSign,
  Package,
  Zap,
  MessageSquare,
  Info,
  Upload,
  Mail,
  Send,
  Inbox
} from "lucide-react";


// Classification colors and badges
const CLASSIFICATION_CONFIG = {
  "Top Performer": { color: "bg-green-500", textColor: "text-green-700", bgLight: "bg-green-100" },
  "High Performer": { color: "bg-blue-500", textColor: "text-blue-700", bgLight: "bg-blue-100" },
  "Solid Performer": { color: "bg-yellow-500", textColor: "text-yellow-700", bgLight: "bg-yellow-100" },
  "Improvement Needed": { color: "bg-orange-500", textColor: "text-orange-700", bgLight: "bg-orange-100" },
  "Performance Risk": { color: "bg-red-500", textColor: "text-red-700", bgLight: "bg-red-100" },
};

// KPI configuration for display
const KPI_CONFIG = {
  volume: { name: "Shipment Volume", weight: 20, icon: Package, description: "Number of PFs handled vs team average" },
  satisfaction: { name: "Customer Satisfaction", weight: 40, icon: Star, description: "Average rating from random customer surveys" },
  completion: { name: "Completion Rate", weight: 20, icon: CheckCircle, description: "Percentage of PFs successfully closed" },
  response_time: { name: "Turnaround Time", weight: 10, icon: Clock, description: "Average days to close a PF" },
  revenue: { name: "Revenue Contribution", weight: 10, icon: DollarSign, description: "Total revenue from assigned PFs" },
  o365_email_activity: { name: "Email Activity (O365)", weight: 0, icon: MessageSquare, description: "Email send/receive/read stats from O365" },
};

function KPICard({ kpi, kpiKey }) {
  const config = KPI_CONFIG[kpiKey];
  const Icon = config?.icon || Target;
  const score = kpi.score || 0;
  const scoreColor = score >= 4 ? "text-green-600" : score >= 3 ? "text-yellow-600" : "text-red-600";
  
  return (
    <div className="bg-slate-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium">{config?.name || kpi.name}</span>
        </div>
        <Badge variant="outline" className="text-xs">{config?.weight}%</Badge>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <span className="text-2xl font-bold">
            {kpi.value !== undefined ? (
              typeof kpi.value === 'number' ? 
                (kpi.unit === '%' || kpiKey === 'completion' ? `${kpi.value}%` : 
                 kpi.unit === 'days' ? `${kpi.value}d` :
                 kpi.unit === 'EUR' ? `€${kpi.value.toLocaleString()}` :
                 kpi.value)
              : kpi.value
            ) : "-"}
          </span>
          {kpi.team_avg !== undefined && (
            <span className="text-xs text-slate-500 ml-2">
              (team avg: {kpi.unit === 'EUR' ? `€${kpi.team_avg.toLocaleString()}` : kpi.team_avg})
            </span>
          )}
        </div>
        <div className={`text-xl font-bold ${scoreColor}`}>
          {score}/5
        </div>
      </div>
      {kpi.survey_count !== undefined && (
        <div className="text-xs text-slate-500 mt-1">
          Based on {kpi.survey_count} customer surveys
        </div>
      )}
      {kpi.note && (
        <div className="text-xs text-blue-600 mt-1 flex items-center gap-1">
          <Info className="h-3 w-3" />
          {kpi.note}
        </div>
      )}
    </div>
  );
}

function EmployeePerformanceCard({ report, onViewDetails }) {
  const config = CLASSIFICATION_CONFIG[report.classification] || CLASSIFICATION_CONFIG["Solid Performer"];
  
  return (
    <Card 
      className="hover:shadow-lg transition-shadow cursor-pointer" 
      onClick={() => onViewDetails(report)}
      data-testid={`employee-card-${report.employee_id}`}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
              <User className="h-6 w-6 text-slate-500" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{report.employee_name}</h3>
              <p className="text-sm text-slate-500">{report.employee_email}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{report.final_score}</div>
            <Badge className={`${config.color} text-white text-xs`}>
              {report.classification}
            </Badge>
          </div>
        </div>
        
        {/* Mini KPI bars */}
        <div className="space-y-2">
          {["satisfaction", "volume", "completion", "response_time"].map(key => {
            const kpi = report.kpis?.[key];
            if (!kpi) return null;
            const score = kpi.score || 0;
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-24">{KPI_CONFIG[key]?.name}</span>
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${score >= 4 ? 'bg-green-500' : score >= 3 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${(score / 5) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium w-6">{score}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
          <span className="text-slate-500">
            <Package className="h-4 w-4 inline mr-1" />
            {report.summary?.total_pfs || 0} PFs
          </span>
          <span className="text-slate-500" data-testid={`staff-survey-stats-${report.employee_email || report.employee_id || 'unknown'}`}>
            <Star className="h-4 w-4 inline mr-1" />
            {report.summary?.avg_rating || 0} avg ·{" "}
            {(report.kpis?.satisfaction?.surveys_responded ?? report.summary?.survey_count) || 0}
            /{report.kpis?.satisfaction?.surveys_sent || 0} responses
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function HRPerformanceAutoPage() {
  const { token, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Period selection
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  
  // Data
  const [reports, setReports] = useState([]);
  const [classifications, setClassifications] = useState({});
  const [teamAverages, setTeamAverages] = useState({});
  const [detailReport, setDetailReport] = useState(null);
  
  // O365 Stats
  const [o365Stats, setO365Stats] = useState([]);
  const [o365DailyStats, setO365DailyStats] = useState([]);
  const [o365Summary, setO365Summary] = useState(null);
  const [uploadingO365, setUploadingO365] = useState(false);

  // KPI Weights (admin-editable)
  const [weights, setWeights] = useState({
    shipment_volume: 20,
    customer_satisfaction: 40,
    completion_rate: 20,
    response_time: 10,
    revenue_contribution: 10,
  });
  const [weightsMeta, setWeightsMeta] = useState({ is_default: true, updated_by: null, updated_at: null });
  const [savingWeights, setSavingWeights] = useState(false);
  const weightTotal = Object.values(weights).reduce((s, v) => s + Number(v || 0), 0);
  const weightsValid = Math.abs(weightTotal - 100) < 0.5;

  const monthNames = ["", "January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/hr-performance/auto/calculate/${selectedYear}/${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReports(response.data.reports || []);
      setClassifications(response.data.classifications || {});
      setTeamAverages(response.data.team_averages || {});
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      toast.error("Failed to load performance reports");
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth]);

  const fetchO365Stats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/hr-performance/o365-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setO365Stats(response.data.stats || []);
      setO365Summary(response.data.summary || null);
      // Store daily stats separately
      if (response.data.daily_stats && response.data.daily_stats.length > 0) {
        setO365DailyStats(response.data.daily_stats);
      }
    } catch (error) {
      console.error("Failed to fetch O365 stats:", error);
    }
  }, [token]);

  const fetchWeights = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/hr-performance/settings/weights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const w = r.data.weights || {};
      // Backend stores as 0-1 floats; UI works in whole-percent integers
      setWeights({
        shipment_volume: Math.round((w.shipment_volume ?? 0.20) * 100),
        customer_satisfaction: Math.round((w.customer_satisfaction ?? 0.40) * 100),
        completion_rate: Math.round((w.completion_rate ?? 0.20) * 100),
        response_time: Math.round((w.response_time ?? 0.10) * 100),
        revenue_contribution: Math.round((w.revenue_contribution ?? 0.10) * 100),
      });
      setWeightsMeta({
        is_default: r.data.is_default,
        updated_by: r.data.updated_by,
        updated_at: r.data.updated_at,
      });
    } catch (e) {
      console.error("Failed to load KPI weights", e);
    }
  }, [token]);

  const saveWeights = async () => {
    if (!weightsValid) {
      toast.error(`Weights must total 100%. Current total: ${weightTotal}%.`);
      return;
    }
    setSavingWeights(true);
    try {
      await axios.put(
        `${API_URL}/hr-performance/settings/weights`,
        {
          shipment_volume: weights.shipment_volume / 100,
          customer_satisfaction: weights.customer_satisfaction / 100,
          completion_rate: weights.completion_rate / 100,
          response_time: weights.response_time / 100,
          revenue_contribution: weights.revenue_contribution / 100,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("KPI weights saved — recalculating reports…");
      await fetchWeights();
      await fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save weights");
    } finally {
      setSavingWeights(false);
    }
  };

  const resetWeights = async () => {
    if (!window.confirm("Reset KPI weights to factory defaults?")) return;
    setSavingWeights(true);
    try {
      await axios.post(
        `${API_URL}/hr-performance/settings/weights/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Weights reset to defaults");
      await fetchWeights();
      await fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset weights");
    } finally {
      setSavingWeights(false);
    }
  };

  const handleO365Import = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    setUploadingO365(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(
        `${API_URL}/hr-performance/o365-stats/import`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success(response.data.message);
      fetchO365Stats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import O365 stats");
    } finally {
      setUploadingO365(false);
      event.target.value = "";
    }
  };

  useEffect(() => {
    fetchReports();
    fetchO365Stats();
    fetchWeights();
  }, [fetchReports, fetchO365Stats, fetchWeights]);

  const navigateMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  // Summary stats
  const avgScore = reports.length > 0 
    ? (reports.reduce((sum, r) => sum + (r.final_score || 0), 0) / reports.length).toFixed(2)
    : 0;
  const totalPFs = reports.reduce((sum, r) => sum + (r.summary?.total_pfs || 0), 0);
  const totalSurveysSent = reports.reduce(
    (sum, r) => sum + (r.kpis?.satisfaction?.surveys_sent || 0), 0
  );
  const totalSurveysResponded = reports.reduce(
    (sum, r) =>
      sum +
      (r.kpis?.satisfaction?.surveys_responded ?? r.kpis?.satisfaction?.survey_count ?? 0),
    0
  );
  const totalSurveys = teamAverages.total_surveys || totalSurveysResponded;

  if (loading && reports.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004488] text-white px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Award className="h-6 w-6" />
                HR Performance Dashboard
              </h1>
              <p className="text-blue-200 text-sm mt-1 flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Automated KPIs from PF data & customer surveys - No manual input required
              </p>
            </div>
            
            {/* Month Navigator */}
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigateMonth(-1)}
                className="text-white hover:bg-white/20"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="text-center min-w-[150px]">
                <div className="text-lg font-semibold">{monthNames[selectedMonth]} {selectedYear}</div>
                <div className="text-xs text-blue-200">Performance Period</div>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigateMonth(1)}
                className="text-white hover:bg-white/20"
                data-testid="next-month-btn"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                onClick={fetchReports} 
                className="text-white border-white/30 hover:bg-white/10 ml-4"
                data-testid="refresh-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Recalculate
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800">Automated Performance Tracking</h3>
              <p className="text-sm text-blue-700 mt-1">
                All KPIs are calculated automatically from existing data. Customer satisfaction scores come from 
                <strong> random automated surveys</strong> sent to ~18% of customers after invoice - staff cannot 
                predict which shipments will be rated. Staff can view their own feedback in the "My Feedback" tab.
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Employees</p>
                  <p className="text-3xl font-bold">{reports.length}</p>
                </div>
                <Users className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Score</p>
                  <p className="text-3xl font-bold">{avgScore}</p>
                </div>
                <BarChart3 className="h-10 w-10 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Top Performers</p>
                  <p className="text-3xl font-bold text-green-600">{classifications["Top Performer"] || 0}</p>
                </div>
                <Award className="h-10 w-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total PFs</p>
                  <p className="text-3xl font-bold">{totalPFs}</p>
                </div>
                <Package className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Surveys</p>
                  <p className="text-3xl font-bold" data-testid="hr-surveys-stat">{totalSurveysResponded}</p>
                  <p className="text-xs text-slate-400">of {totalSurveysSent} sent</p>
                </div>
                <MessageSquare className="h-10 w-10 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="rankings" data-testid="tab-rankings">Rankings</TabsTrigger>
            <TabsTrigger value="o365" data-testid="tab-o365">
              <Mail className="h-4 w-4 mr-1" />
              O365 Stats
            </TabsTrigger>
            <TabsTrigger value="kpi-weights" data-testid="tab-kpi">KPI Weights</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Classification Distribution */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Performance Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  {Object.entries(CLASSIFICATION_CONFIG).map(([cls, config]) => (
                    <div key={cls} className="flex-1 text-center">
                      <div className={`${config.bgLight} rounded-lg p-4`}>
                        <div className={`text-3xl font-bold ${config.textColor}`}>
                          {classifications[cls] || 0}
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{cls}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Employee Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reports.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center text-slate-500">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No performance data for this period</p>
                    <p className="text-sm mt-2">Reports are automatically generated from PF activity</p>
                  </CardContent>
                </Card>
              ) : (
                reports.map(report => (
                  <EmployeePerformanceCard 
                    key={report.employee_id} 
                    report={report}
                    onViewDetails={setDetailReport}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Rankings Tab */}
          <TabsContent value="rankings">
            <Card>
              <CardHeader>
                <CardTitle>Performance Rankings - {monthNames[selectedMonth]} {selectedYear}</CardTitle>
                <CardDescription>Based on automated KPIs from PFs and customer surveys</CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Rank</th>
                      <th className="text-left py-3 px-4">Employee</th>
                      <th className="text-center py-3 px-4">Score</th>
                      <th className="text-center py-3 px-4">Satisfaction</th>
                      <th className="text-center py-3 px-4">PFs</th>
                      <th className="text-center py-3 px-4">Completion</th>
                      <th className="text-left py-3 px-4">Classification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report) => {
                      const config = CLASSIFICATION_CONFIG[report.classification] || CLASSIFICATION_CONFIG["Solid Performer"];
                      const rank = report.rank || 0;
                      return (
                        <tr key={report.employee_id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                              rank === 2 ? 'bg-slate-300 text-slate-700' :
                              rank === 3 ? 'bg-orange-300 text-orange-800' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {rank}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium">{report.employee_name}</div>
                            <div className="text-sm text-slate-500">{report.employee_email}</div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-xl font-bold">{report.final_score}</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Star className={`h-4 w-4 ${report.kpis?.satisfaction?.value >= 4 ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
                              <span className={report.kpis?.satisfaction?.score >= 4 ? 'text-green-600' : report.kpis?.satisfaction?.score >= 3 ? 'text-yellow-600' : 'text-red-600'}>
                                {report.kpis?.satisfaction?.value || '-'}
                              </span>
                            </div>
                            <div className="text-xs text-slate-400">
                              {(report.kpis?.satisfaction?.surveys_responded ?? report.kpis?.satisfaction?.survey_count) || 0}
                              /{report.kpis?.satisfaction?.surveys_sent || 0} responses
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="font-medium">{report.summary?.total_pfs || 0}</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={report.kpis?.completion?.score >= 4 ? 'text-green-600' : report.kpis?.completion?.score >= 3 ? 'text-yellow-600' : 'text-red-600'}>
                              {report.kpis?.completion?.value || 0}%
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`${config.color} text-white`}>
                              {report.classification}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* O365 Email Stats Tab */}
          <TabsContent value="o365">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-blue-600" />
                      O365 Email Activity Statistics
                    </CardTitle>
                    <CardDescription>
                      Import email activity data from Microsoft 365 Admin Center exports
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleO365Import}
                      className="hidden"
                      id="o365-import"
                    />
                    <label htmlFor="o365-import">
                      <Button 
                        variant="outline" 
                        disabled={uploadingO365}
                        className="cursor-pointer"
                        asChild
                      >
                        <span>
                          {uploadingO365 ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Import O365 CSV
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary Stats */}
                {o365Summary && (
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <Send className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{o365Summary.total_emails_sent?.toLocaleString()}</p>
                      <p className="text-xs text-blue-600">Emails Sent</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <Inbox className="h-6 w-6 mx-auto text-green-600 mb-2" />
                      <p className="text-2xl font-bold text-green-700">{o365Summary.total_emails_received?.toLocaleString()}</p>
                      <p className="text-xs text-green-600">Emails Received</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <CheckCircle className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                      <p className="text-2xl font-bold text-purple-700">{o365Summary.total_emails_read?.toLocaleString()}</p>
                      <p className="text-xs text-purple-600">Emails Read</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <Users className="h-6 w-6 mx-auto text-slate-600 mb-2" />
                      <p className="text-2xl font-bold text-slate-700">{o365Stats.length}</p>
                      <p className="text-xs text-slate-600">{o365Stats[0]?.user_email ? 'Users Tracked' : 'Days Tracked'}</p>
                    </div>
                  </div>
                )}

                {/* User Table or Daily Counts Table */}
                {o365Stats.length > 0 && o365Stats[0]?.user_email ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3">User</th>
                          <th className="text-center p-3">Sent</th>
                          <th className="text-center p-3">Received</th>
                          <th className="text-center p-3">Read</th>
                          <th className="text-center p-3">Read Rate</th>
                          <th className="text-center p-3">Meetings Created</th>
                          <th className="text-center p-3">Last Activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {o365Stats.map((stat, idx) => {
                          const readRate = stat.receive_count > 0 
                            ? Math.round((stat.read_count / stat.receive_count) * 100) 
                            : 0;
                          return (
                            <tr key={idx} className="border-b hover:bg-slate-50">
                              <td className="p-3">
                                <div>
                                  <p className="font-medium">{stat.display_name || stat.user_email}</p>
                                  <p className="text-xs text-muted-foreground">{stat.user_email}</p>
                                </div>
                              </td>
                              <td className="text-center p-3 font-mono">
                                <span className="text-blue-600">{stat.send_count}</span>
                              </td>
                              <td className="text-center p-3 font-mono">
                                <span className="text-green-600">{stat.receive_count}</span>
                              </td>
                              <td className="text-center p-3 font-mono">
                                <span className="text-purple-600">{stat.read_count}</span>
                              </td>
                              <td className="text-center p-3">
                                <Badge className={`${
                                  readRate >= 80 ? 'bg-green-100 text-green-700' :
                                  readRate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {readRate}%
                                </Badge>
                              </td>
                              <td className="text-center p-3 font-mono">{stat.meeting_created_count}</td>
                              <td className="text-center p-3 text-xs text-muted-foreground">
                                {stat.last_activity_date || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (o365Stats.length > 0 || o365DailyStats.length > 0) ? (
                  <div className="overflow-x-auto">
                    <p className="text-sm text-muted-foreground mb-3">Daily email activity (aggregate counts)</p>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3">Date</th>
                          <th className="text-center p-3">Sent</th>
                          <th className="text-center p-3">Received</th>
                          <th className="text-center p-3">Read</th>
                          <th className="text-center p-3">Read Rate</th>
                          <th className="text-center p-3">Meetings Created</th>
                          <th className="text-center p-3">Meetings Interacted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(o365DailyStats.length > 0 ? o365DailyStats : o365Stats).map((stat, idx) => {
                          const readRate = stat.receive_count > 0 
                            ? Math.round((stat.read_count / stat.receive_count) * 100) 
                            : 0;
                          return (
                            <tr key={idx} className="border-b hover:bg-slate-50">
                              <td className="p-3 font-medium">{stat.report_date || '-'}</td>
                              <td className="text-center p-3 font-mono">
                                <span className="text-blue-600">{stat.send_count}</span>
                              </td>
                              <td className="text-center p-3 font-mono">
                                <span className="text-green-600">{stat.receive_count}</span>
                              </td>
                              <td className="text-center p-3 font-mono">
                                <span className="text-purple-600">{stat.read_count}</span>
                              </td>
                              <td className="text-center p-3">
                                <Badge className={`${
                                  readRate >= 80 ? 'bg-green-100 text-green-700' :
                                  readRate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {readRate}%
                                </Badge>
                              </td>
                              <td className="text-center p-3 font-mono">{stat.meeting_created_count}</td>
                              <td className="text-center p-3 font-mono">{stat.meeting_interacted_count}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">No O365 Stats Imported</p>
                    <p className="text-sm mt-1">
                      Export Email Activity report from O365 Admin Center (User Detail or User Counts) and import here
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* KPI Weights Tab */}
          <TabsContent value="kpi-weights">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <CardTitle>KPI Weight Distribution</CardTitle>
                    <CardDescription>
                      How each metric contributes to the final performance score.
                      {isAdmin
                        ? " As admin you can edit these — changes are persisted and applied on the next recalculation."
                        : " Read-only — only admins can modify weights."}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge
                      className={`${weightsValid ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"} border-0`}
                      data-testid="kpi-weight-total-badge"
                    >
                      Total: {Math.round(weightTotal)}%
                    </Badge>
                    {weightsMeta.updated_by && !weightsMeta.is_default && (
                      <span className="text-xs text-slate-500">
                        edited by {weightsMeta.updated_by.split("@")[0]}
                        {weightsMeta.updated_at ? ` · ${new Date(weightsMeta.updated_at).toLocaleDateString()}` : ""}
                      </span>
                    )}
                    {weightsMeta.is_default && (
                      <span className="text-xs text-slate-400 italic">defaults</span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { key: "shipment_volume", display: KPI_CONFIG.volume },
                    { key: "customer_satisfaction", display: KPI_CONFIG.satisfaction },
                    { key: "completion_rate", display: KPI_CONFIG.completion },
                    { key: "response_time", display: KPI_CONFIG.response_time },
                    { key: "revenue_contribution", display: KPI_CONFIG.revenue },
                  ].map(({ key, display }) => {
                    const Icon = display.icon;
                    const value = weights[key];
                    return (
                      <div key={key} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <Icon className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold">{display.name}</h4>
                          <p className="text-sm text-slate-500 mt-1">{display.description}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <input
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={value}
                            disabled={!isAdmin}
                            onChange={(e) => setWeights({ ...weights, [key]: Number(e.target.value) })}
                            className="w-40 accent-blue-600 disabled:opacity-50"
                            data-testid={`kpi-weight-slider-${key}`}
                          />
                          <div className="relative">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={value}
                              disabled={!isAdmin}
                              onChange={(e) => setWeights({ ...weights, [key]: Math.max(0, Math.min(100, Number(e.target.value || 0))) })}
                              className="w-20 px-2 py-1 pr-6 text-right border border-slate-300 rounded-md text-sm font-semibold disabled:bg-slate-100 disabled:opacity-60"
                              data-testid={`kpi-weight-input-${key}`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!weightsValid && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <AlertTriangle className="h-4 w-4 inline mr-2" />
                    Weights total <strong>{Math.round(weightTotal)}%</strong> — adjust the values so they add up to 100% before saving.
                  </div>
                )}

                {isAdmin && (
                  <div className="mt-6 flex items-center justify-end gap-2 border-t pt-4">
                    <Button
                      variant="outline"
                      onClick={resetWeights}
                      disabled={savingWeights}
                      data-testid="kpi-weight-reset-btn"
                    >
                      Reset to defaults
                    </Button>
                    <Button
                      onClick={saveWeights}
                      disabled={savingWeights || !weightsValid}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="kpi-weight-save-btn"
                    >
                      {savingWeights && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Save weights
                    </Button>
                  </div>
                )}

                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Tip: Customer Satisfaction
                  </h4>
                  <p className="text-sm text-amber-700 mt-2">
                    Customer Satisfaction is a survey-driven KPI. Surveys are sent automatically to ~18% of customers
                    after their shipment is invoiced. The random selection ensures staff cannot game the system
                    by providing better service only to customers they know will be surveyed. Lowering this weight
                    makes the score more dependent on volume / completion / revenue.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Detail Report Modal */}
      {detailReport && (
        <Dialog open onOpenChange={() => setDetailReport(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {detailReport.employee_name} - Performance Report
              </DialogTitle>
              <DialogDescription>
                {monthNames[detailReport.period_month]} {detailReport.period_year} - Automated calculation
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Final Score */}
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-6">
                <div>
                  <p className="text-sm text-slate-500">Final Performance Score</p>
                  <p className="text-5xl font-bold">{detailReport.final_score}</p>
                </div>
                <Badge className={`${CLASSIFICATION_CONFIG[detailReport.classification]?.color || 'bg-slate-500'} text-white text-lg py-2 px-4`}>
                  {detailReport.classification}
                </Badge>
              </div>

              {/* KPIs Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(detailReport.kpis || {}).map(([key, kpi]) => (
                  <KPICard key={key} kpi={kpi} kpiKey={key} />
                ))}
              </div>

              {/* Data Sources Info */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Data Sources
                </h4>
                <div className="text-sm text-blue-700 mt-2 grid grid-cols-2 gap-2">
                  <div>Position Folders analyzed: <strong>{detailReport.data_sources?.pfs_analyzed || 0}</strong></div>
                  <div>Customer surveys received: <strong>{detailReport.data_sources?.surveys_received || 0}</strong></div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
